<template>
  <div class="move-equipment-modal">
    <div
      v-if="!equipmentItem"
      class="text-center py-8 text-surface-500"
    >
      <p>No equipment item selected</p>
    </div>

    <div v-else>
      <div class="mb-6">
        <h4 class="text-sm font-semibold text-surface-700 mb-2">
          Moving Equipment:
        </h4>
        <div class="bg-surface-50 p-4 rounded-lg border border-surface-200">
          <p class="font-medium text-lg">
            {{ equipmentItem.equipment_list_content?.display_name || equipmentItem.equipment_list_content?.name || 'Unknown Item' }}
          </p>
          <div class="flex gap-4 mt-2 text-sm text-surface-600">
            <span>Quantity: {{ equipmentItem.quantity }}</span>
            <span>Unit Cost: £{{ formatCurrency(equipmentItem.unit_cost || 0) }}</span>
            <span>{{ formatCostModel(equipmentItem.cost_model) }}</span>
          </div>
        </div>
      </div>

      <div class="mb-4">
        <label class="block text-sm font-medium text-surface-700 mb-2">
          Select Subhire:
        </label>
        <Select
          v-model="selectedSubhireId"
          :options="subhireOptions"
          option-label="label"
          option-value="value"
          placeholder="Choose a subhire..."
          class="w-full"
          :clearable="true"
        />
        <p
          v-if="subhireOptions.length === 0"
          class="text-xs text-surface-500 mt-1"
        >
          No other subhires available
        </p>
      </div>

      <div class="mb-4">
        <div class="flex items-center gap-2">
          <div class="flex-1 border-t border-surface-200" />
          <span class="text-xs text-surface-500">OR</span>
          <div class="flex-1 border-t border-surface-200" />
        </div>
      </div>

      <div class="mb-4">
        <Button
          label="Create New Subhire"
          icon="pi pi-plus"
          severity="secondary"
          outlined
          class="w-full"
          :loading="isCreatingNew"
          @click="handleCreateNewSubhire"
        />
        <p class="text-xs text-surface-500 mt-2">
          Create a new subhire and move this equipment to it
        </p>
      </div>

      <div class="flex justify-end gap-2 pt-4 border-t border-surface-200">
        <Button
          label="Cancel"
          severity="secondary"
          outlined
          @click="handleCancel"
        />
        <Button
          label="Move Equipment"
          icon="pi pi-arrow-right"
          :disabled="!isFormValid"
          :loading="isMoving"
          @click="handleMove"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useToastService } from '@/composables/useToastService';
import { projectStore } from '../projectStore';
import {
  addSubHireLine,
  createEquipmentListSubHire,
  deleteSubHireLine
} from '@/services/subhireEquipmentListService';

const props = defineProps({
  equipmentItem: {
    type: Object,
    required: false,
    default: null
  },
  currentSubhireId: {
    type: [String, Number],
    required: false,
    default: null
  },
  availableSubhires: {
    type: Array,
    required: false,
    default: () => []
  }
});

const emit = defineEmits(['close', 'moved']);

const toast = useToastService();

const selectedSubhireId = ref(null);
const isMoving = ref(false);
const isCreatingNew = ref(false);

// Note: Removed selectedDestination - now only supports moving to existing or new subhires

const subhireOptions = computed(() => {
  // Filter out the current subhire if we're moving from one
  const otherSubhires = props.availableSubhires.filter(
    sh => !props.currentSubhireId || sh.id !== parseInt(props.currentSubhireId)
  );

  return otherSubhires.map(sh => ({
    label: `${sh.subhire_number || `SH-${sh.id}`} - ${sh.supplier?.company_name || sh.supplier?.name || 'Unknown Supplier'}`,
    value: sh.id
  }));
});

const isFormValid = computed(() => {
  return !!selectedSubhireId.value;
});

const formatCurrency = (value) => {
  return parseFloat(value || 0).toFixed(2);
};

const formatCostModel = (costModel) => {
  const modelMap = {
    per_day: 'Per Day',
    per_week: 'Per Week',
    flat: 'Flat Rate',
    per_project: 'Per Project'
  };
  return modelMap[costModel] || costModel;
};

const handleCancel = () => {
  emit('close');
};

const handleMove = async () => {
  if (!isFormValid.value || !props.equipmentItem) {
    return;
  }

  isMoving.value = true;

  try {
    const projectId = projectStore.currentProjectId;
    const equipmentListId = projectStore.currentEquipmentListId;

    if (!projectId || !equipmentListId) {
      throw new Error('Project ID and Equipment List ID are required');
    }

    // Backend doesn't support "move" - we need to delete from source and create in target
    
    // Step 1: Delete line from source subhire (if there is one)
    if (props.currentSubhireId) {
      await deleteSubHireLine(projectId, equipmentListId, props.currentSubhireId, props.equipmentItem.id);
    }

    // Step 2: Create line in target subhire
    const payload = {
      equipment_list_content_id: props.equipmentItem.equipment_list_content_id,
      quantity: props.equipmentItem.quantity,
      unit_cost: props.equipmentItem.unit_cost || 0,
      cost_model: props.equipmentItem.cost_model || 'per_day',
      tax_rate: props.equipmentItem.tax_rate || 0,
      notes: props.equipmentItem.notes || null
    };

    await addSubHireLine(projectId, equipmentListId, selectedSubhireId.value, payload);

    const moveData = {
      equipmentItem: props.equipmentItem,
      destination: 'subhire',
      targetSubhireId: selectedSubhireId.value,
      sourceSubhireId: props.currentSubhireId || null
    };

    // Emit moved event so parent can update state
    emit('moved', moveData);

    toast.add({
      severity: 'success',
      summary: 'Equipment Moved',
      detail: 'Equipment moved to subhire',
      life: 3000
    });

    emit('close');
  } catch (error) {
    console.error('Error moving equipment:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.message || error.message || 'Failed to move equipment',
      life: 3000
    });
  } finally {
    isMoving.value = false;
  }
};

const handleCreateNewSubhire = async () => {
  if (!props.equipmentItem) {
    return;
  }

  isCreatingNew.value = true;

  try {
    const projectId = projectStore.currentProjectId;
    const equipmentListId = projectStore.currentEquipmentListId;

    if (!projectId || !equipmentListId) {
      throw new Error('Project ID and Equipment List ID are required');
    }

    // Delete from source subhire first (if there is one)
    if (props.currentSubhireId) {
      await deleteSubHireLine(projectId, equipmentListId, props.currentSubhireId, props.equipmentItem.id);
    }

    // Create a new subhire with default values
    const payload = {
      name: null,
      supplier_id: null,
      status: 'pending',
      supplier_reference: null,
      our_po_number: null,
      rental_start_datetime: null,
      rental_end_datetime: null,
      notes: null,
      transport_required: false,
      // New transport model fields
      inbound_method: null,
      outbound_method: null,
      delivery_datetime: null,
      delivery_cost: 0,
      delivery_address_id: null,
      collection_datetime: null,
      collection_cost: 0,
      collection_address_id: null,
      transport_notes: null,
      lines: [
        {
          equipment_list_content_id: props.equipmentItem.equipment_list_content_id,
          quantity: props.equipmentItem.quantity,
          unit_cost: props.equipmentItem.unit_cost || 0,
          cost_model: props.equipmentItem.cost_model || 'per_day',
          tax_rate: props.equipmentItem.tax_rate || 0,
          notes: props.equipmentItem.notes || null
        }
      ]
    };

    const newSubhire = await createEquipmentListSubHire(projectId, equipmentListId, payload);

    const newSubhireId = newSubhire?.id;

    // Move equipment to the new subhire
    const moveData = {
      equipmentItem: props.equipmentItem,
      destination: 'subhire',
      targetSubhireId: newSubhireId,
      sourceSubhireId: props.currentSubhireId || null
    };

    emit('moved', moveData);

    // Refresh subhires tab visibility
    if (projectStore.refreshSubhiresCheck) {
      await projectStore.refreshSubhiresCheck();
    }

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'New subhire created and equipment moved',
      life: 3000
    });

    emit('close');
  } catch (error) {
    console.error('Error creating new subhire:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.message || error.message || 'Failed to create new subhire',
      life: 3000
    });
  } finally {
    isCreatingNew.value = false;
  }
};
</script>

<style scoped>
.move-equipment-modal {
  min-width: 500px;
}
</style>
